import React from 'react';
import { Card, Button, Space, Tag, Popconfirm, Message, Tooltip } from '@arco-design/web-react';
import { IconCalendar, IconLocation, IconUser, IconPushpin, IconDelete, IconEdit } from '@arco-design/web-react/icon';
import { Event } from '../../models/Event';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import axios from 'axios';
import { endpoints } from '../../api/endpoints';
import { trackEvent } from '../../analytics/track';
import { formatEventDateTime, isEventUpcoming } from '../../utils/eventDateTime';
import './EventCard.css';

interface EventCardProps {
  event: Event;
  onUpdate?: () => void;
  onEdit?: (event: Event) => void;
}

export const EventCard: React.FC<EventCardProps> = ({ event, onUpdate, onEdit }) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user.user);
  const isAdmin = user?.isAdmin || false;

  const formatDate = (dateString: string) => formatEventDateTime(dateString, event.timezone);

  const isPastEvent = !isEventUpcoming(event.event_date, event.timezone);

  const handleRSVP = () => {
    trackEvent("event_rsvp_click", { event_id: event.id });
    window.open(event.rsvp_url, '_blank', 'noopener,noreferrer');
  };

  const handlePin = async () => {
    try {
      if (event.is_pinned) {
        await axios.post(endpoints.admin.unpinEvent(event.id));
        Message.success(t('notification_messages.event_unpinned_success'));
      } else {
        await axios.post(endpoints.admin.pinEvent(event.id));
        Message.success(t('notification_messages.event_pinned_success'));
      }
      onUpdate?.();
    } catch (error) {
      Message.error(t('notification_messages.event_pin_error'));
    }
  };

  const handlePublishToggle = async () => {
    try {
      if (event.is_published) {
        await axios.post(endpoints.admin.unpublishEvent(event.id));
        Message.success(t('notification_messages.event_unpublished_success'));
      } else {
        await axios.post(endpoints.admin.publishEvent(event.id));
        Message.success(t('notification_messages.event_published_success'));
      }
      onUpdate?.();
    } catch (error) {
      Message.error(event.is_published 
        ? t('notification_messages.event_unpublish_error')
        : t('notification_messages.event_publish_error')
      );
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(endpoints.admin.deleteEvent(event.id));
      Message.success(t('notification_messages.event_deleted_success'));
      onUpdate?.();
    } catch (error) {
      Message.error(t('notification_messages.event_delete_error'));
    }
  };

  return (
    <Card
      className={`event-card ${event.is_pinned ? 'event-card--pinned' : ''}`}
      hoverable
      cover={
        event.image_url ? (
          <div className="event-card__image-wrapper">
            <img
              src={event.image_url}
              alt={event.title}
              className="event-card__image"
            />
            {event.is_pinned && (
              <Tag className="event-card__featured-tag">
                {t('events.featured')}
              </Tag>
            )}
          </div>
        ) : (
          event.is_pinned && (
            <div className="event-card__no-image">
              <Tag className="event-card__featured-tag">
                {t('events.featured')}
              </Tag>
            </div>
          )
        )
      }
    >
      <Card.Meta
        title={
          <div className="event-card__title">
            {event.title}
          </div>
        }
        description={
          <div className="event-card__content">
            <div className="event-card__info">
              <Space direction="vertical" size="small">
                <div className="event-card__info-item">
                  <IconCalendar />
                  <span>{formatDate(event.event_date)}</span>
                </div>
                <div className="event-card__info-item">
                  <IconLocation />
                  <span>{event.location}</span>
                </div>
                {event.organizer && (
                  <div className="event-card__info-item">
                    <IconUser />
                    <span>{event.organizer}</span>
                  </div>
                )}
              </Space>
            </div>
            <div className="event-card__description">
              {event.description}
            </div>
            <div className="event-card__actions">
              {!isPastEvent && event.rsvp_url && (
                <Button 
                  type="primary" 
                  shape="round"
                  onClick={handleRSVP}
                  className="event-card__button event-card__button--primary"
                >
                  {t('events.rsvp')}
                </Button>
              )}
              {!isPastEvent && !event.rsvp_url && isAdmin && (
                <Tooltip content={t('events.rsvp_url_missing_admin')}>
                  <Button
                    type="secondary"
                    shape="round"
                    disabled
                    className="event-card__button event-card__button--secondary"
                  >
                    {t('events.rsvp')}
                  </Button>
                </Tooltip>
              )}
              {isAdmin && (
                <Space>
                  <Button
                    type="secondary"
                    shape="round"
                    icon={<IconEdit />}
                    onClick={() => onEdit?.(event)}
                    className="event-card__button event-card__button--secondary"
                  >
                    {t('buttons.edit')}
                  </Button>
                  <Button
                    type={event.is_pinned ? 'outline' : 'secondary'}
                    shape="round"
                    icon={<IconPushpin />}
                    onClick={handlePin}
                    className="event-card__button event-card__button--secondary"
                  >
                    {event.is_pinned ? t('events.unpin') : t('events.pin')}
                  </Button>
                  <Popconfirm
                    title={event.is_published ? t('events.unpublish_confirm') : t('events.publish_confirm')}
                    onOk={handlePublishToggle}
                    okText={t('buttons.yes')}
                    cancelText={t('buttons.no')}
                    okButtonProps={{ 
                      className: "event-card__popconfirm-button event-card__popconfirm-button--ok" 
                    }}
                    cancelButtonProps={{ 
                      className: "event-card__popconfirm-button event-card__popconfirm-button--cancel" 
                    }}
                  >
                    <Button 
                      type="secondary"
                      shape="round"
                      className="event-card__button event-card__button--secondary"
                    >
                      {event.is_published ? t('events.unpublish') : t('events.publish')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={t('events.delete_confirm')}
                    onOk={handleDelete}
                    okText={t('buttons.yes')}
                    cancelText={t('buttons.no')}
                    okButtonProps={{ 
                      className: "event-card__popconfirm-button event-card__popconfirm-button--ok" 
                    }}
                    cancelButtonProps={{ 
                      className: "event-card__popconfirm-button event-card__popconfirm-button--cancel" 
                    }}
                  >
                    <Button 
                      type="primary" 
                      status="danger" 
                      shape="round"
                      icon={<IconDelete />}
                      className="event-card__button event-card__button--danger"
                    >
                      {t('buttons.delete')}
                    </Button>
                  </Popconfirm>
                </Space>
              )}
            </div>
          </div>
        }
      />
    </Card>
  );
};

